import { NgClass } from '@angular/common'
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Injector,
  OnInit,
  Signal,
  computed,
  effect,
  inject,
  input,
  runInInjectionContext,
} from '@angular/core'

import { FlxStyleModule, FlxThemeModule, FlxWrapperStyleDirective } from '@flowx/angular-theme'
import { FlxIconModule } from '@flowx/angular-ui-toolkit'
import { StatsCardTemplateConfig } from '@flowx/core-sdk'

import {
  getBoolExpressionSignal,
  getComputedValueSignal,
  getContentIcon,
  getDynamicPropSignal,
} from '../helpers'

@Component({
  selector: 'flx-stats-card-wrapper',
  hostDirectives: [
    { directive: FlxWrapperStyleDirective, inputs: ['flxWrapperStyle: templateConfig'] },
  ],
  imports: [NgClass, FlxThemeModule, FlxStyleModule, FlxIconModule],
  templateUrl: './stats-card-wrapper.component.html',
  styleUrl: './stats-card-wrapper.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxStatsCardWrapperComponent implements OnInit, AfterViewInit {
  readonly #injector = inject(Injector)
  readonly #flxWrapperStyle = inject(FlxWrapperStyleDirective)

  templateConfig = input.required<StatsCardTemplateConfig>()

  displayOptions = computed(() => this.templateConfig().displayOptions)
  flowxProps = computed(() => this.displayOptions().flowxProps)

  // expressions
  isHidden: Signal<boolean>
  // dynamic props
  value: Signal<string>

  readonly iconPath = computed(() => {
    const icon = this.flowxProps()?.icon
    if (!icon?.value) {
      return null
    }
    return getContentIcon(icon)
  })

  readonly trendClass = computed(() => {
    const trend = this.flowxProps()?.trend
    if (trend === 'up') {
      return 'stats-card__trend--up'
    }
    if (trend === 'down') {
      return 'stats-card__trend--down'
    }
    return 'stats-card__trend--neutral'
  })

  readonly trendArrow = computed(() => {
    const trend = this.flowxProps()?.trend
    if (trend === 'up') {
      return '▲'
    }
    if (trend === 'down') {
      return '▼'
    }
    return '—'
  })

  ngOnInit(): void {
    runInInjectionContext(this.#injector, () => {
      const props = this.flowxProps()
      this.isHidden = getBoolExpressionSignal(props.hide, this.templateConfig())
      this.value = props.isComputedValue
        ? getComputedValueSignal(props.value || '', this.templateConfig())
        : getDynamicPropSignal(props.value || '', this.templateConfig())
    })
  }

  ngAfterViewInit(): void {
    runInInjectionContext(this.#injector, () => {
      effect(() => {
        this.#flxWrapperStyle.toggleDisplay(this.isHidden())
      })
    })
  }
}
